import { ApiClient } from '@utils/apiClient';
import { handleApiResult } from '@utils/apiErrorHandler';
import { WebsitePage, WebsitePageDetail, WebsiteSource } from '../types';
import { unwrap } from './faqService';

const BASE_PATH = '/knowledge-base/websites';

const request = async <T>(response: Response, fallback: string): Promise<T> =>
  unwrap<T>(await handleApiResult<T>(response, fallback), fallback) as T;

export const websiteService = {
  list: async (knowledgeBaseId: string): Promise<WebsiteSource[]> => {
    const params = new URLSearchParams({ knowledge_base: knowledgeBaseId });
    const data = await request<WebsiteSource[]>(
      await ApiClient.get(`${BASE_PATH}/?${params.toString()}`),
      'Failed to load the website'
    );
    return data ?? [];
  },
  // Starts the first import right away; nothing is used before the selection
  create: async (knowledgeBaseId: string, rootUrl: string): Promise<WebsiteSource> =>
    request<WebsiteSource>(
      await ApiClient.post(`${BASE_PATH}/`, {
        knowledge_base: knowledgeBaseId,
        root_url: rootUrl,
      }),
      'Failed to add the website'
    ),
  remove: async (id: string): Promise<void> => {
    await request<void>(
      await ApiClient.delete(`${BASE_PATH}/${id}/`),
      'Failed to remove the website'
    );
  },
  crawl: async (id: string): Promise<WebsiteSource> =>
    request<WebsiteSource>(
      await ApiClient.post(`${BASE_PATH}/${id}/crawl/`, {}),
      'Failed to start the import'
    ),
  pages: async (id: string): Promise<WebsitePage[]> => {
    const data = await request<WebsitePage[]>(
      await ApiClient.get(`${BASE_PATH}/${id}/pages/`),
      'Failed to load the pages'
    );
    return data ?? [];
  },
  // Included pages are chunked, excluded ones are removed from the knowledge base
  select: async (
    id: string,
    selection: { included?: string[]; excluded?: string[] }
  ): Promise<WebsitePage[]> => {
    const data = await request<WebsitePage[]>(
      await ApiClient.post(`${BASE_PATH}/${id}/selection/`, selection),
      'Failed to save the selection'
    );
    return data ?? [];
  },
  page: async (pageId: string): Promise<WebsitePageDetail> =>
    request<WebsitePageDetail>(
      await ApiClient.get(`/knowledge-base/website-pages/${pageId}/`),
      'Failed to load the page'
    ),
};
